import {
  IntegrationsDemoPage,
  IntegrationsRouteModal,
  isPartnerFeatureOn,
  SETTING_PAGE_ID_URL_PARAM_NAME,
} from '@portals/framework';
import { ReactComponent as Box } from '@portals/icons/linear/box.svg';
import { ReactComponent as CalendarIcon } from '@portals/icons/linear/calendar.svg';
import { ReactComponent as CloudConnection } from '@portals/icons/linear/cloud-connection.svg';
import { ReactComponent as Code } from '@portals/icons/linear/code.svg';
import { ReactComponent as ColorSwatch } from '@portals/icons/linear/color-swatch.svg';
import { ReactComponent as Danger } from '@portals/icons/linear/danger.svg';
import { ReactComponent as Element4 } from '@portals/icons/linear/element-4.svg';
import { ReactComponent as EmptyWallet } from '@portals/icons/linear/empty-wallet.svg';
import { ReactComponent as FolderCloud } from '@portals/icons/linear/folder-cloud.svg';
import { ReactComponent as Health } from '@portals/icons/linear/health.svg';
import { ReactComponent as Hierarchy3 } from '@portals/icons/linear/hierarchy-3.svg';
import { ReactComponent as MonitorMobbile } from '@portals/icons/linear/monitor-mobbile.svg';
import { ReactComponent as Receipt2 } from '@portals/icons/linear/receipt-2.svg';
import { ReactComponent as Setting2 } from '@portals/icons/linear/setting-2.svg';
import { ReactComponent as Shop } from '@portals/icons/linear/shop.svg';
import { ReactComponent as TaskSquare } from '@portals/icons/linear/task-square.svg';
import { ReactComponent as Task } from '@portals/icons/linear/task.svg';
import { ReactComponent as Ticket } from '@portals/icons/linear/ticket.svg';
import { ReactComponent as TruckFast } from '@portals/icons/linear/truck-fast.svg';
import { ReactComponent as UserSquare } from '@portals/icons/linear/user-square.svg';
import {
  CategoryRouteItem,
  PartnerConfigurationType,
  RoutesMap,
  TenantType,
} from '@portals/types';

import { ReactComponent as Connect } from './assets/img/icons/sidebar/connect.svg';
import { ReactComponent as CustomersIcon } from './assets/img/icons/sidebar/customers.svg';
import { ReactComponent as DashboardIcon } from './assets/img/icons/sidebar/dashboard.svg';
import { ReactComponent as HomeIcon } from './assets/img/icons/sidebar/home.svg';
import { ReactComponent as ModelsIcon } from './assets/img/icons/sidebar/models.svg';
import { ReactComponent as WhitelabelIcon } from './assets/img/icons/sidebar/whitelabel.svg';
import { OnboardingWizard } from './components/OnboardingWizard';
import { PartnerToPartnerStatus } from './components/PartnerToPartnerStatus';
import { TicketsCounter, TicketsIndicator } from './components/TicketsCounter';
import { Branding } from './pages/branding/Branding';
import { CreditProviders } from './pages/credit-providers';
import { Customers } from './pages/customers';
import Dashboard from './pages/dashboard';
import { DevCenter } from './pages/dev-center';
import { Devices } from './pages/devices/Devices';
import { Files } from './pages/files';
import { Home } from './pages/home';
import Incidents from './pages/incidents';
import Models from './pages/models';
import { Organizations } from './pages/organizations';
import { Partners } from './pages/partners/Partners';
import { ProductsCatalogPage } from './pages/products-catalog/ProductsCatalogPage';
import Rules from './pages/rules';
import { Settings } from './pages/settings/Settings';
import { CreateTaxGroup } from './pages/settings/store-settings/tax-groups/create-tax-group';
import { StoreListingsPage } from './pages/store-listings/StoreListingsPage';
import { EcommerceOverview } from './pages/store-management/pages/ecommerce-overview/EcommerceOverview';
import { FinanceOverview } from './pages/store-management/pages/finance-overview/FinanceOverview';
import { Invoices } from './pages/store-management/pages/invoices/Invoices';
import { Orders } from './pages/store-management/pages/orders/Orders';
import { PurchasedProducts } from './pages/store-management/pages/purchased-products/PurchasedProducts';
import { Shipping } from './pages/store-management/pages/shipping/Shipping';
import { Subscriptions } from './pages/store-management/pages/subscriptions/Subscriptions';
import { StoreManagement } from './pages/store-management/StoreManagement';
import { SupportOverview } from './pages/support-overview';
import { Tickets } from './pages/tickets';
import { CreditProviders as CreditProvidersRouteModal } from './route-modals/CreditProviders';
import { Customer } from './route-modals/Customer';
import { Order } from './route-modals/Order/Order';
import { PurchasedProductRoutePanel } from './route-panels/product/PurchasedProductRoutePanel';
import { StoreListingRoutePanel } from './route-panels/store-listings/StoreListingRoutePanel';
import { SubscriptionRoutePanel } from './route-panels/subscriptions/SubscriptionRoutePanel';

const dashboard: CategoryRouteItem<PartnerConfigurationType>[] = [
  {
    id: 'dashboard',
    headerId: '',
    canAccessRoute: ({ configuration }) => {
      if (!configuration) return false;

      return configuration.pricing_plan.features.device_models_limit > 0;
    },
    childRoutes: [
      {
        path: ['/', '/home'],
        id: 'sidebar.home',
        icon: HomeIcon,
        component: Home,
        canAccessRoute: ({ configuration, isAdmin }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'home') &&
          !isPartnerFeatureOn(configuration?.feature_flags, 'hubs') &&
          isAdmin,
      },
      {
        path: ({ configuration, isAdmin }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'home') && isAdmin
            ? ['/dashboard']
            : ['/', '/dashboard'],
        id: 'sidebar.dashboard',
        icon: DashboardIcon,
        component: Dashboard,
        canAccessRoute: ({ configuration }) =>
          !isPartnerFeatureOn(configuration?.feature_flags, 'hubs'),
      },
    ],
  },
  {
    id: 'configuration',
    headerId: 'sidebar.configuration',
    childRoutes: [
      {
        path: ['/models', '/models/:id/*'],
        id: 'sidebar.models',
        icon: ModelsIcon,
        component: Models,
        canAccessRoute: ({ canView, hasSupportSeat, configuration }) => {
          if (
            configuration?.pricing_plan?.features?.device_models_limit === 0
          ) {
            return false;
          }

          return hasSupportSeat || canView(['models']);
        },
      },
      {
        path: ['/devices', '/devices/claimed', '/devices/unclaimed'],
        id: 'sidebar.devices',
        icon: MonitorMobbile,
        component: Devices,
        canAccessRoute: ({ canView, hasSupportSeat, configuration }) => {
          if (
            configuration?.pricing_plan?.features?.device_models_limit === 0
          ) {
            return false;
          }

          return hasSupportSeat || canView(['models', 'fulfillment']);
        },
      },
      {
        path: ['/files', '/files/:id'],
        id: 'sidebar.files',
        icon: FolderCloud,
        component: Files,
        canAccessRoute: ({ canView, hasSupportSeat, configuration }) => {
          if (
            configuration?.pricing_plan?.features?.device_models_limit === 0
          ) {
            return false;
          }

          return hasSupportSeat || canView('models');
        },
      },
      {
        path: ['/rules/:id'],
        id: 'sidebar.incidentRules',
        component: Rules,
        isVisibleInSidebar: () => false,
        canAccessRoute: ({ configuration, canView, hasSupportSeat }) => {
          if (
            configuration?.pricing_plan?.features?.device_models_limit === 0
          ) {
            return false;
          }

          return hasSupportSeat || canView('models');
        },
      },
      {
        path: '/catalog/*',
        id: 'sidebar.catalog',
        icon: ColorSwatch,
        component: ProductsCatalogPage,
        canAccessRoute: ({ hasSupportSeat, canView, configuration }) => {
          return (
            (hasSupportSeat || canView('store_management')) &&
            configuration?.[TenantType.Partner]?.classification !==
              'channel_partner'
          );
        },
      },
      {
        path: ['/branding', '/branding/:type'],
        id: 'sidebar.branding',
        icon: WhitelabelIcon,
        component: Branding,
        canAccessRoute: ({ isAdmin, configuration }) =>
          isAdmin &&
          configuration?.[TenantType.Partner]?.classification !==
            'channel_partner',
      },
    ],
  },
  {
    id: 'yourStore',
    headerId: 'sidebar.yourStore',
    canAccessRoute: ({ configuration }) =>
      configuration?.pricing_plan?.features?.store === 1,
    childRoutes: [
      {
        path: '/products/*',
        id: 'sidebar.products',
        icon: Box,
        component: StoreListingsPage,
        canAccessRoute: ({ hasSupportSeat, canView }) =>
          hasSupportSeat || canView('store_management'),
      },
      {
        path: ['/store-management', '/store-management/*'],
        id: 'sidebar.storeManagement',
        icon: Shop,
        canAccessRoute: ({ canView }) =>
          canView(['store_management', 'finance', 'fulfillment']),
        component: StoreManagement,
        children: [
          {
            path: '/store-management/subscriptions/*',
            id: 'subscriptions',
            component: Subscriptions,
          },
          {
            path: [
              '/store-management/purchased-products',
              '/store-management/purchased-products/:tab',
            ],
            id: 'purchasedProducts',
            component: PurchasedProducts,
          },
          {
            path: ['/store-management/orders', '/store-management/orders/:tab'],
            id: 'orders',
            component: Orders,
          },
          {
            path: [
              '/store-management/shipping',
              '/store-management/shipping/:tab',
            ],
            id: 'shipping',
            component: Shipping,
          },
          {
            path: '/store-management/invoices/*',
            id: 'invoices',
            component: Invoices,
          },
          {
            path: [
              '/store-management/ecommerce-overview',
              '/store-management/ecommerce-overview/:tab',
            ],
            id: 'ecommerceOverview',
            component: FinanceOverview,
            canAccessRoute: ({ configuration }) =>
              isPartnerFeatureOn(configuration?.feature_flags, 'finance'),
          },
        ],
      },
    ],
  },
  {
    id: 'supportAndMonitoring',
    headerId: 'sidebar.supportAndMonitoring',
    canAccessRoute: ({ hasSupportSeat, isAdmin, configuration }) =>
      isAdmin ||
      (hasSupportSeat &&
        Boolean(configuration?.portal_capabilities?.support_center)),
    childRoutes: [
      {
        path: ({ configuration }) => {
          if (
            configuration?.pricing_plan?.features?.device_models_limit === 0 &&
            !isPartnerFeatureOn(configuration?.feature_flags, 'hubs')
          ) {
            return ['/', '/support-overview'];
          }

          return ['/support-overview'];
        },
        id: 'sidebar.supportOverview',
        icon: Health,
        component: SupportOverview,
      },
      {
        path: ['/organizations'],
        id: 'sidebar.supportedCustomers',
        icon: UserSquare,
        component: Organizations,
        canAccessRoute: ({ hasSupportSeat, isAdmin }) =>
          hasSupportSeat || isAdmin,
      },
      {
        path: ['/tickets', '/tickets/active', '/tickets/all', '/tickets/:id'],
        id: 'sidebar.tickets',
        icon: Ticket,
        component: Tickets,
        badgeComponent: TicketsCounter,
        indicatorComponent: TicketsIndicator,
        canAccessRoute: ({ configuration }) => {
          if (!configuration) return false;

          return configuration.pricing_plan.features.device_models_limit > 0;
        },
      },
      {
        path: ['/incidents', '/incidents/:id'],
        id: 'sidebar.incidents',
        icon: Danger,
        component: Incidents,
      },
    ],
  },
  {
    id: 'yourConnections',
    headerId: 'sidebar.yourConnections',
    canAccessRoute: ({ canView, hasSupportSeat }) =>
      hasSupportSeat || canView(['customers', 'fulfillment', 'finance']),
    childRoutes: [
      {
        path: ['/customers'],
        id: 'sidebar.customers',
        icon: CustomersIcon,
        isVisibleInSidebar: ({ configuration }) =>
          !isPartnerFeatureOn(configuration?.feature_flags, 'hubs'),
        canAccessRoute: ({ configuration }) =>
          configuration?.[TenantType.Partner]?.classification !==
          'channel_partner',
        component: Customers,
      },
      {
        id: 'sidebar.partners',
        path: '/partners/*',
        icon: CloudConnection,
        component: Partners,
        canAccessRoute: ({ isAdmin }) => isAdmin,
        badgeComponent: PartnerToPartnerStatus,
      },
      {
        id: 'sidebar.creditProviders',
        path: ['/credit-providers'],
        icon: EmptyWallet,
        component: CreditProviders,
        canAccessRoute: ({ configuration, isAdmin }) =>
          isAdmin &&
          isPartnerFeatureOn(configuration?.feature_flags, 'credit_providers'),
      },
    ],
  },
  {
    id: 'integrations',
    headerId: '',
    canAccessRoute: ({ configuration }) =>
      isPartnerFeatureOn(configuration?.feature_flags, 'integrations_demo'),
    childRoutes: [
      {
        path: ['/integrations', '/integrations/:integration'],
        id: 'sidebar.integrations',
        icon: Hierarchy3,
        component: IntegrationsDemoPage,
      },
    ],
  },
  {
    id: 'devCenter',
    headerId: '',
    canAccessRoute: ({ configuration, canView, hasSupportSeat }) => {
      if (configuration?.pricing_plan?.features?.device_models_limit === 0) {
        return false;
      }

      return hasSupportSeat || canView('models');
    },
    childRoutes: [
      {
        path: ['/dev-center'],
        hasBorder: true,
        id: 'sidebar.devCenter',
        icon: Code,
        component: DevCenter,
      },
    ],
  },
  {
    id: 'settings',
    headerId: '',
    canAccessRoute: ({ isAdmin }) => isAdmin,
    childRoutes: [
      {
        path: ['/settings', `/settings/:${SETTING_PAGE_ID_URL_PARAM_NAME}/*`],
        id: 'sidebar.settings',
        icon: Setting2,
        component: Settings,
        children: [
          {
            path: '/settings/store/create-tax-group',
            component: CreateTaxGroup,
          },
        ],
      },
    ],
  },

  // TODO: Hubs layout
  {
    id: 'hubsPlaceholder',
    headerId: '',
    canAccessRoute: ({ configuration }) =>
      isPartnerFeatureOn(configuration?.feature_flags, 'hubs'),
    childRoutes: [
      {
        path: ['/', '/home'],
        id: 'sidebar.gettingStarted',
        icon: Task,
        component: Home,
        canAccessRoute: ({ configuration, isAdmin }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'home') &&
          isAdmin &&
          configuration.pricing_plan.features.device_models_limit > 0,
      },
      {
        path: ({ configuration, isAdmin }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'home') && isAdmin
            ? ['/dashboard']
            : ['/', '/dashboard'],
        id: 'sidebar.homeOverview',
        icon: Element4,
        component: Dashboard,
        canAccessRoute: ({ configuration }) =>
          configuration.pricing_plan.features.device_models_limit > 0,
      },
      {
        path: '/partners/*',
        id: 'partners',
        component: Partners,
        icon: Connect,
        canAccessRoute: ({ isAdmin }) => isAdmin,
        isVisibleInSidebar: () => false,
      },
      {
        path: ['/customers', '/customers/*'],
        id: 'sidebar.customers',
        icon: CustomersIcon,
        isVisibleInSidebar: () => false,
        canAccessRoute: ({ configuration }) =>
          configuration?.[TenantType.Partner]?.classification !==
          'channel_partner',
        component: Customers,
      },
      {
        path: ['/store-management/orders', '/store-management/orders/*'],
        id: 'orders',
        component: Orders,
        icon: TaskSquare,
        canAccessRoute: ({ configuration, canView }) => {
          return (
            configuration?.pricing_plan?.features?.store === 1 &&
            isPartnerFeatureOn(configuration?.feature_flags, 'hubs') &&
            canView(['finance', 'fulfillment'])
          );
        },
      },
      {
        path: '/store-management/invoices/*',
        id: 'invoices',
        component: Invoices,
        icon: Receipt2,
        canAccessRoute: ({ configuration, canView }) =>
          configuration?.pricing_plan?.features?.store === 1 &&
          isPartnerFeatureOn(configuration?.feature_flags, 'hubs') &&
          canView('finance'),
      },
      {
        path: ['/store-management/shipping', '/store-management/shipping/*'],
        id: 'shipping',
        component: Shipping,
        icon: TruckFast,
        canAccessRoute: ({ configuration, canView }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'hubs') &&
          configuration?.pricing_plan?.features?.store === 1 &&
          canView(['finance', 'fulfillment']),
      },
      {
        path: '/store-management/subscriptions/*',
        id: 'subscriptions',
        icon: CalendarIcon,
        component: Subscriptions,
        canAccessRoute: ({ configuration, canView }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'hubs') &&
          configuration?.pricing_plan?.features?.store === 1 &&
          canView(['store_management', 'finance', 'fulfillment']),
      },
      {
        path: [
          '/store-management/purchased-products',
          '/store-management/purchased-products/*',
        ],
        id: 'purchasedProducts',
        icon: Box,
        component: PurchasedProducts,
        canAccessRoute: ({ configuration, canView }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'hubs') &&
          configuration?.pricing_plan?.features?.store === 1 &&
          canView(['store_management', 'finance', 'fulfillment']),
      },
      {
        path: '/store-management/ecommerce-overview',
        id: 'ecommerceOverview',
        icon: Element4,
        component: EcommerceOverview,
        canAccessRoute: ({ configuration }) =>
          isPartnerFeatureOn(configuration?.feature_flags, 'finance'),
      },
    ],
  },
];

const routeModals: RoutesMap<PartnerConfigurationType>['routeModals'] = [
  {
    id: 'customer',
    path: '/m/customers/:customer_id',
    component: Customer,
    backgroundPath: '/customers',
    generatePath: ([customerId]: [string]) => `/m/customers/${customerId}`,
  },
  {
    id: 'order',
    path: '/m/order/:order_id',
    component: Order,
    backgroundPath: '/store-management/orders',
    generatePath: ([orderId]: [string]) => `/m/order/${orderId}`,
  },
  {
    id: 'credit-providers',
    path: '/m/credit-providers',
    component: CreditProvidersRouteModal,
    backgroundPath: '/',
    generatePath: () => `/m/credit-providers`,
  },
  {
    id: 'integrations',
    path: ['/m/integrations', '/m/integrations/:integration'],
    component: IntegrationsRouteModal,
    backgroundPath: '/integrations',
    canAccessRoute: ({ configuration }) =>
      Boolean(configuration?.feature_flags?.integrations_demo),
    generatePath: () => `/m/integrations`,
  },
];

const onboarding = [
  {
    id: 'onboarding',
    path: '/onboarding',
    component: OnboardingWizard,
  },
];

const routePanels: RoutesMap<PartnerConfigurationType>['routePanels'] = [
  {
    id: 'store_listing',
    path: '/p/products/:store_listing_slug',
    component: StoreListingRoutePanel,
    backgroundPath: '/products',
    generatePath: ([store_listing_slug]: string[]) =>
      `/p/products/${store_listing_slug}`,
  },
  {
    id: 'subscription',
    path: '/p/subscription/:subscription_id',
    component: SubscriptionRoutePanel,
    backgroundPath: '/store-management/subscriptions',
    generatePath: ([subscriptionId]: string[]) =>
      `/p/subscription/${subscriptionId}`,
  },
  {
    id: 'purchased_product',
    path: '/p/purchased_product/:purchased_product_id',
    component: PurchasedProductRoutePanel,
    backgroundPath: '/store-management/purchased-products',
    generatePath: ([purchasedProductId]: string[]) =>
      `/p/purchased_product/${purchasedProductId}`,
  },
];

export const routes: RoutesMap<PartnerConfigurationType> = {
  dashboard,
  routeModals,
  onboarding,
  routePanels,
};
